import { proxy } from "valtio";
import { toastError } from "../../common/tools-app-wrapper";
import {
  Changelog,
  CreateDraftChangelogRequest,
  DraftChangelogUpdate,
  EntityRegistry,
  FieldConfigShort,
  PublishableEntries,
  PublishableTypes,
  PublishStateResponse,
  RowDiff,
} from "@renewmap/model";
import { hasValueChanged } from "@/common/table/cell-renderers";

export interface PublishState {
  changelogs: Changelog[];
  types: Record<PublishableTypes, PublishableEntries>;
  invalidEntityRecordIds: Partial<Record<PublishableTypes, string[]>>;
  unpublishedEntityRecordIds: Partial<Record<PublishableTypes, string[]>>;
  entityRegistry: EntityRegistry;
  fieldConfigs: FieldConfigShort[];
  changedColumns: Record<PublishableTypes, Set<string>>;
  deletingInvalidEntityRecords: Partial<Record<PublishableTypes, boolean>>;
  publishIds: Set<string>;
  markAllDiffsState: {
    marking: boolean;
    markedIds: Partial<Record<PublishableTypes, string[]>>;
  };
  score: number;
  canInsert: boolean;
}

class PublishManager {
  public publishState: PublishState = proxy({
    changelogs: [] as Changelog[],
    types: {} as Record<PublishableTypes, PublishableEntries>,
    invalidEntityRecordIds: {} as Partial<Record<PublishableTypes, string[]>>,
    unpublishedEntityRecordIds: {} as Partial<
      Record<PublishableTypes, string[]>
    >,
    entityRegistry: {} as EntityRegistry,
    fieldConfigs: [] as FieldConfigShort[],
    publishIds: new Set<string>(),
    markAllDiffsState: {
      marking: false,
      markedIds: {} as Partial<Record<PublishableTypes, string[]>>,
    },
    score: 0,
    changedColumns: {} as Record<PublishableTypes, Set<string>>,
    deletingInvalidEntityRecords: {} as Partial<
      Record<PublishableTypes, boolean>
    >,
    canInsert: false,
  });

  constructor() {}

  public init = (data: PublishStateResponse) => {
    this.publishState.entityRegistry = data.entityRegistry;
    this.publishState.fieldConfigs = data.fieldConfigs;

    this.publishState.changelogs = data.changelogs;
    this.publishState.types = data.types;
    this.publishState.invalidEntityRecordIds = data.invalidEntityRecordIds;
    this.publishState.unpublishedEntityRecordIds =
      data.unpublishedEntityRecordIds;
    this.publishState.canInsert = data.canInsert;
    this.publishState.changedColumns = this.calculateChangedColumns(data.types);
    this.publishState.score = data.score;
    this.publishState.publishIds = this.initPublishIds(data.types);
  };

  // Build the initial publishIds set: all diffed IDs.
  private initPublishIds = (
    types: Record<PublishableTypes, PublishableEntries>,
  ): Set<string> => {
    const publishIds = new Set<string>();
    Object.keys(types).forEach((tableName) => {
      const table = tableName as PublishableTypes;
      types[table].entries.forEach((e) => publishIds.add((e as RowDiff).id));
    });
    return publishIds;
  };

  // Update the publishIds set based on selected and deselected IDs
  // -> new state needed for update count badges to reactively update
  public setPublishIds = (selectedIds: string[], deselectedIds: string[]) => {
    const next = new Set(this.publishState.publishIds);
    selectedIds.forEach((id) => next.add(id));
    deselectedIds.forEach((id) => next.delete(id));
    this.publishState.publishIds = next;
  };

  private calculateChangedColumns = (
    types: Record<PublishableTypes, PublishableEntries>,
  ) => {
    const result = {} as Record<PublishableTypes, Set<string>>;

    Object.keys(types).forEach((key) => {
      const { entries } = types[key as PublishableTypes];

      const changedColumns = new Set<string>();
      for (const entry of entries ?? []) {
        // Access values from RowDiff structure
        const values = (entry as RowDiff).values || {};
        for (const key of Object.keys(values)) {
          if (changedColumns.has(key)) {
            continue;
          }

          if (hasValueChanged(key, values[key]) !== "nochange") {
            changedColumns.add(key);
          }
        }
      }

      result[key as PublishableTypes] = changedColumns;
    });

    return result;
  };

  public publish = async (): Promise<boolean> => {
    const res = await fetch("/api/publish/prod", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        publishIds: [...this.publishState.publishIds],
      }),
    });

    if (!res.ok) {
      const errorData = (await res.json()) as { message?: string };
      const errorDetails = errorData?.message || res.statusText;

      toastError("Failed to publish", `[${res.status}] ${errorDetails}`);
      return false;
    }

    return true;
  };

  public deleteAllInvalidEntityRecords = async () => {
    // Set loading state for all tables
    const tableNames = Object.keys(
      this.publishState.invalidEntityRecordIds,
    ) as PublishableTypes[];
    for (const tableName of tableNames) {
      this.publishState.deletingInvalidEntityRecords[tableName] = true;
    }

    try {
      const res = await fetch(`/api/draft/invalid-entity-records`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(this.publishState.invalidEntityRecordIds),
      });

      if (!res.ok) {
        const errorData = (await res.json()) as { message?: string };
        const errorDetails = errorData?.message || res.statusText;

        toastError(
          "Failed to delete invalid entity records",
          `[${res.status}] ${errorDetails}`,
        );
        return;
      }

      // Refresh data in background without full page reload
      await this.refreshData();
    } finally {
      // Clear loading state for all tables
      for (const tableName of tableNames) {
        this.publishState.deletingInvalidEntityRecords[tableName] = false;
      }
    }
  };

  private refreshData = async () => {
    const res = await fetch(`/api/publish/diff`);

    if (!res.ok) {
      const errorData = (await res.json()) as { message?: string };
      const errorDetails = errorData?.message || res.statusText;

      toastError("Failed to refresh data", `[${res.status}] ${errorDetails}`);
      return;
    }

    this.init((await res.json()) as PublishStateResponse);
  };

  public getEntry = (type: PublishableTypes, id: string) => {
    return this.publishState.types[type].entries.find(
      (entry) => entry.id === id,
    );
  };

  public insertDraftUpdates = async (updates: DraftChangelogUpdate[]) => {
    const body: CreateDraftChangelogRequest = { updates };
    const res = await fetch(`/api/draft/changelog`, {
      body: JSON.stringify(body),
      method: "POST",
    });

    if (!res.ok) {
      const errorData = (await res.json()) as { message?: string };
      const errorDetails = errorData?.message || res.statusText;

      toastError(
        "Failed to insert changelog updates",
        `[${res.status}] ${errorDetails}`,
      );
      return;
    }

    // don't do it again - naughty
    this.publishState.canInsert = false;
  };

  public markAllDiffs = async () => {
    this.publishState.markAllDiffsState.marking = true;
    try {
      const res = await fetch(`/api/draft/mark-all-diffs-to-be-published`, {
        method: "POST",
      });
      if (res.ok) {
        const data = (await res.json()) as {
          ids: Partial<Record<PublishableTypes, string[]>>;
        };
        this.publishState.markAllDiffsState.markedIds = data.ids;
      }
      await this.refreshData();
    } finally {
      this.publishState.markAllDiffsState.marking = false;
    }
  };

  public unmarkAllDiffs = async () => {
    this.publishState.markAllDiffsState.marking = true;
    try {
      await fetch(`/api/draft/unmark-all-diffs-to-be-published`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ids: this.publishState.markAllDiffsState.markedIds,
        }),
      });
      this.publishState.markAllDiffsState.markedIds = {};
      await this.refreshData();
    } finally {
      this.publishState.markAllDiffsState.marking = false;
    }
  };
}

export const publishManager = new PublishManager();
